import React from 'react';
import {
    Box,
    TextField,
    Button,
    Paper,
    Typography,
    Grid,
    InputAdornment,
    alpha,
    useTheme,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Stack
} from "@mui/material";
import { DateRange, FilterList, CalendarToday } from "@mui/icons-material";

interface ReportFiltersProps {
    filters: { fromDate: string; toDate: string; year?: string };
    setFilters: React.Dispatch<React.SetStateAction<{ fromDate: string; toDate: string; year?: string }>>;
    onApply?: () => void;
    onReset?: () => void;
    showYear?: boolean;
}

const ReportFilters: React.FC<ReportFiltersProps> = ({
    filters,
    setFilters,
    onApply,
    onReset,
    showYear = false
}) => {
    const theme = useTheme();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
        const { name, value } = e.target;
        setFilters({ ...filters, [name as string]: value });
    };

    const handleYearChange = (event: any) => {
        const year = event.target.value;
        setFilters({ ...filters, year });
    };

    const handleApply = () => {
        if (onApply) {
            onApply();
        }
    };

    const handleReset = () => {
        setFilters({ fromDate: "", toDate: "", year: "" });
        if (onReset) {
            onReset();
        }
    };

    // Generate year options (last 10 years + current year + next 2 years)
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 13 }, (_, i) => currentYear - 5 + i);

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                mb: 4,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                background: `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.05)}`
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        mr: 2
                    }}
                >
                    <FilterList sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                        Filter Report
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.25 }}>
                        Select date range or year to filter the report data
                    </Typography>
                </Box>
            </Box>

            <Grid container spacing={2.5} alignItems="flex-end">
                <Grid item xs={12} sm={12} md={3}>
                    <TextField
                        label="From Date"
                        name="fromDate"
                        type="date"
                        size="small"
                        fullWidth
                        value={filters.fromDate || ""}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <DateRange sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                },
                            }
                        }}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={3}>
                    <TextField
                        label="To Date"
                        name="toDate"
                        type="date"
                        size="small"
                        fullWidth
                        value={filters.toDate || ""}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <DateRange sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                },
                            }
                        }}
                    />
                </Grid>

                {showYear && (<Grid item xs={12} sm={12} md={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Year (Optional)</InputLabel>
                        <Select
                            value={filters.year || ""}
                            label="Year (Optional)"
                            onChange={handleYearChange}
                            name="year"
                            startAdornment={
                                <InputAdornment position="start">
                                    <CalendarToday sx={{ color: theme.palette.primary.main, fontSize: 18, ml: 1 }} />
                                </InputAdornment>
                            }
                            sx={{
                                borderRadius: 1.5,
                                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                },
                            }}
                        >
                            <MenuItem value="">All Years</MenuItem>
                            {yearOptions.map((year) => (
                                <MenuItem key={year} value={year.toString()}>
                                    {year}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>)}

                <Grid item xs={12} sm={6} md={2}>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleApply}
                        disabled={!filters.fromDate && !filters.toDate && !filters.year}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 1.5
                        }}
                    >
                        Apply Filters
                    </Button>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleReset}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 1.5
                        }}
                    >
                        Reset
                    </Button>
                </Grid>
            </Grid>

            {}
            {(filters.fromDate || filters.toDate || filters.year) && (
                <Box
                    sx={{
                        mt: 2.5,
                        pt: 2,
                        borderTop: `1px dashed ${alpha(theme.palette.divider, 0.5)}`,
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            Active filters:
                        </Typography>
                        {filters.year && (
                            <Chip
                                label={`Year: ${filters.year}`}
                                size="small"
                                onDelete={() => setFilters({ ...filters, year: "" })}
                                sx={{
                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                    color: theme.palette.primary.main,
                                    fontWeight: 500,
                                    '& .MuiChip-deleteIcon': {
                                        color: theme.palette.primary.main,
                                        fontSize: 16
                                    }
                                }}
                            />
                        )}
                        {filters.fromDate && (
                            <Chip
                                label={`From: ${new Date(filters.fromDate).toLocaleDateString()}`}
                                size="small"
                                onDelete={() => setFilters({ ...filters, fromDate: "" })}
                                sx={{
                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                    color: theme.palette.primary.main,
                                    fontWeight: 500,
                                }}
                            />
                        )}
                        {filters.toDate && (
                            <Chip
                                label={`To: ${new Date(filters.toDate).toLocaleDateString()}`}
                                size="small"
                                onDelete={() => setFilters({ ...filters, toDate: "" })}
                                sx={{
                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                    color: theme.palette.primary.main,
                                    fontWeight: 500,
                                }}
                            />
                        )}
                    </Stack>
                </Box>
            )}
        </Paper>
    );
};

export default ReportFilters;